import React, { useState, useEffect, useCallback } from "react";
import {
  useUpdateDiagnosticAnswerMutation,
  useSubmitDiagnosticFormMutation,
} from "../../../services/api";
import QuestionRenderer from "../components/QuestionRenderer";
import { getQuestionData, calculateProgress } from "../utils/diagnosticHelpers";

/**
 * Form Fill View Component
 * Shows the form for filling out
 */
const FormFillView = ({ assignment, submission, onBack, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [updateAnswer] = useUpdateDiagnosticAnswerMutation();
  const [submitForm, { isLoading: isSubmitting }] = useSubmitDiagnosticFormMutation();

  const template = assignment.template;
  const questions = template?.questions || [];
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];

  // Initialize answers from submission
  useEffect(() => {
    if (submission?.answers) {
      const answerMap = {};
      submission.answers.forEach((ans) => {
        answerMap[ans.questionIndex] = ans.value;
      });
      setAnswers(answerMap);
    }
  }, [submission]);

  // Handle answer change with auto-save
  const handleAnswerChange = useCallback(
    async (value, valueLabel = null, valueLabels = null) => {
      setAnswers((prev) => ({ ...prev, [currentIndex]: value }));

      if (submission?._id) {
        setIsSaving(true);
        try {
          await updateAnswer({
            id: submission._id,
            questionIndex: currentIndex,
            value,
            valueLabel,
            valueLabels,
          });
        } catch (error) {
          console.error("Failed to save answer:", error);
        }
        setIsSaving(false);
      }
    },
    [currentIndex, submission, updateAnswer]
  );

  // Handle next
  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Handle previous
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!submission?._id) return;

    try {
      await submitForm(submission._id).unwrap();
      onComplete();
    } catch (error) {
      console.error("Failed to submit form:", error);
      alert("Failed to submit form. Please try again.");
    }
  };

  const progress = calculateProgress(answers, totalQuestions);
  const questionData = currentQuestion ? getQuestionData(currentQuestion) : null;

  return (
    <>
      <div className="diagnostic-card">
        <div className="card-head">
          <h2 className="section-heading">
            {assignment.evaluationType === "self"
              ? "Self Evaluation"
              : `Evaluating ${assignment.target?.firstName} ${assignment.target?.lastName}`}
          </h2>
          <div className="step-indicator">{template?.title}</div>
        </div>

        <div className="progress-row">
          <p className="helper-text progress-copy">
            {template?.instructions || "Please answer all questions honestly and thoughtfully."}
          </p>

          <div className="progress-top">
            <span className="progress-sub">
              Statement{" "}
              <span className="progress-number">
                {currentIndex + 1}
              </span>{" "}
              of {totalQuestions}
            </span>
            <span className="progress-meta">
              {progress}% Completed {isSaving && "(Saving...)"}
            </span>
          </div>

          <div className="progress">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="diagnostic-card">
        <div className="q-title">
          Statement
        </div>

        {questionData && (
          <>
            <div className="question-text">{questionData.text}</div>

            {questionData.description && (
              <p className="question-desc">{questionData.description}</p>
            )}

            <QuestionRenderer
              question={questionData}
              value={answers[currentIndex]}
              onChange={handleAnswerChange}
              disabled={false}
            />
          </>
        )}

        <div className="nav-row">
          <button
            type="button"
            className="ghost-btn"
            onClick={currentIndex === 0 ? onBack : handlePrev}
          >
            {currentIndex === 0 ? "← Exit" : "← Back"}
          </button>

          {currentIndex === totalQuestions - 1 ? (
            <button
              type="button"
              className="submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit →"}
            </button>
          ) : (
            <button
              type="button"
              className="submit-btn go-next"
              onClick={handleNext}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default FormFillView;
